import axiosInstance from '../../services/axiosInstance';
import {
  RecipeCorrectionAnalysis,
  RecipeCorrectionApplyResult,
  RecipeCorrectionPrompt,
  RecipeCorrectionRunSummary,
  RecipeCorrectionSession,
  RecipeCorrectionSnapshot,
  RecipeFieldValues,
  ScalarKey,
} from './types';

function apiError(error: any, fallback: string): Error {
  return new Error(error?.response?.data?.message || error?.message || fallback);
}

export async function fetchRecipeCorrectionStatus(): Promise<{ available: boolean; openai: boolean }> {
  try {
    const { data } = await axiosInstance.get('/api/recipe-correction/status');
    return data;
  } catch (error) {
    throw apiError(error, 'Kunde inte kontrollera OpenAI-status.');
  }
}

export async function fetchRecipeCorrectionPrompt(): Promise<RecipeCorrectionPrompt> {
  try {
    const { data } = await axiosInstance.get('/api/recipe-correction/prompt');
    return data;
  } catch (error) {
    throw apiError(error, 'Kunde inte hämta masterprompten.');
  }
}

export async function saveRecipeCorrectionPrompt(systemPrompt: string): Promise<RecipeCorrectionPrompt> {
  try {
    const { data } = await axiosInstance.put('/api/recipe-correction/prompt', { systemPrompt });
    return data;
  } catch (error) {
    throw apiError(error, 'Kunde inte spara masterprompten.');
  }
}

export async function fetchRecipeCorrectionSession(assetId: string): Promise<RecipeCorrectionSession> {
  try {
    const { data } = await axiosInstance.get(`/api/recipe-correction/assets/${assetId}/session`);
    return data;
  } catch (error) {
    throw apiError(error, 'Kunde inte ladda receptet.');
  }
}

export async function analyzeRecipeCorrection(assetId: string): Promise<RecipeCorrectionAnalysis> {
  try {
    const { data } = await axiosInstance.post(
      `/api/recipe-correction/assets/${assetId}/analyze`,
      {},
      { timeout: 180000 },
    );
    return data;
  } catch (error) {
    throw apiError(error, 'Kunde inte analysera receptet.');
  }
}

export async function importAnalyzeRecipeCorrection(assetId: string, file: File): Promise<RecipeCorrectionAnalysis> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axiosInstance.post(
      `/api/recipe-correction/assets/${assetId}/import-analyze`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      },
    );
    return data;
  } catch (error) {
    throw apiError(error, 'Kunde inte importera och analysera filen.');
  }
}

export async function applyRecipeCorrection(params: {
  assetId: string;
  mode: 'correct' | 'import';
  proposed: RecipeFieldValues;
  acceptedScalars: ScalarKey[];
  acceptedIngredientIndexes: number[];
  acceptedStepIndexes: number[];
  changes?: RecipeCorrectionAnalysis['changes'];
  sourceFileName?: string;
}): Promise<RecipeCorrectionApplyResult> {
  try {
    const { data } = await axiosInstance.post(
      `/api/recipe-correction/assets/${params.assetId}/apply`,
      {
        mode: params.mode,
        proposed: params.proposed,
        acceptedScalars: params.acceptedScalars,
        acceptedIngredientIndexes: params.acceptedIngredientIndexes,
        acceptedStepIndexes: params.acceptedStepIndexes,
        changes: params.changes || [],
        sourceFileName: params.sourceFileName,
      },
      { timeout: 60000 },
    );
    return data;
  } catch (error) {
    throw apiError(error, 'Kunde inte spara receptkorrigeringen.');
  }
}

export async function fetchLatestRecipeCorrectionSummary(assetId: string): Promise<RecipeCorrectionRunSummary | null> {
  try {
    const { data } = await axiosInstance.get(`/api/recipe-correction/assets/${assetId}/latest-run`);
    return data?.run || null;
  } catch (error) {
    throw apiError(error, 'Kunde inte hämta senaste receptändringen.');
  }
}

export async function fetchLatestRecipeCorrectionSnapshot(assetId: string): Promise<RecipeCorrectionSnapshot | null> {
  try {
    const { data } = await axiosInstance.get(`/api/recipe-correction/assets/${assetId}/latest-run`, {
      params: { full: '1' },
    });
    return data?.run || null;
  } catch (error) {
    throw apiError(error, 'Kunde inte hämta den sparade receptändringen.');
  }
}
